import { doc, setDoc, getDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

export async function saveCompanyCode(companyCode) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'latest', 'profile'), {
    companyCode: companyCode.toUpperCase().trim(),
    email: user.email,
    updatedAt: serverTimestamp()
  }, { merge: true });
  await setDoc(doc(db, 'companies', companyCode.toUpperCase().trim(), 'members', user.uid), {
    uid: user.uid,
    email: user.email,
    joinedAt: serverTimestamp()
  });
}

// Fetch all member data for a given employeeCode
export async function getCompanyData(employeeCode) {
  const code = employeeCode.toUpperCase().trim();

  // Load all members under this company code
  const membersSnap = await getDocs(collection(db, 'companies', code, 'members'));
  if (membersSnap.empty) return null;

  const members = membersSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
  // Only count employees (not HR managers) in the health data
  const employeeUids = members.filter(m => m.role === 'employee' || !m.role).map(m => m.uid);

  const results = {
    code,
    totalEmployees: employeeUids.length,
    totalMembers: members.length,
    blood: [],
    body: [],
    mental: [],
    fitness: []
  };

  await Promise.all(employeeUids.map(async (uid) => {
    try {
      const [bloodSnap, extBloodSnap, bodySnap, mentalSnap, fitnessSnap] = await Promise.all([
        getDoc(doc(db, 'users', uid, 'latest', 'blood')),
        getDoc(doc(db, 'users', uid, 'latest', 'extendedBlood')),
        getDoc(doc(db, 'users', uid, 'latest', 'body')),
        getDoc(doc(db, 'users', uid, 'latest', 'mental')),
        getDoc(doc(db, 'users', uid, 'latest', 'fitness'))
      ]);
      const combined = {
        ...(bloodSnap.exists() ? bloodSnap.data() : {}),
        ...(extBloodSnap.exists() ? extBloodSnap.data() : {})
      };
      if (Object.keys(combined).length > 0) results.blood.push(combined);
      if (bodySnap.exists()) results.body.push(bodySnap.data());
      if (mentalSnap.exists()) results.mental.push(mentalSnap.data());
      if (fitnessSnap.exists()) results.fitness.push(fitnessSnap.data());
    } catch (e) {
      console.error('Error loading member data for uid:', uid, e);
    }
  }));

  return results;
}

export function calcAverage(arr, key) {
  const vals = arr.map(d => parseFloat(d[key])).filter(v => !isNaN(v));
  if (vals.length === 0) return null;
  return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}

export function calcBMIFromData(bodyData) {
  const arr = Array.isArray(bodyData) ? bodyData : [bodyData];
  const bmis = arr.map(b => {
    if (!b || !b.height || !b.weight) return null;
    const h = parseFloat(b.height) / 100;
    return parseFloat(b.weight) / (h * h);
  }).filter(v => v !== null);
  if (bmis.length === 0) return null;
  return (bmis.reduce((a, b) => a + b, 0) / bmis.length).toFixed(1);
}

export function getRiskCategory(stressScore, phq9, gad7, burnoutScore) {
  const stress = parseFloat(stressScore) || 0;
  const phq = parseFloat(phq9) || 0;
  const gad = parseFloat(gad7) || 0;
  const burnout = parseFloat(burnoutScore) || 0;
  const score = (stress / 100 * 40) + (phq / 27 * 20) + (gad / 21 * 20) + (burnout / 100 * 20);
  if (score >= 55) return 'High';
  if (score >= 30) return 'Moderate';
  return 'Low';
}
